import { useEffect, useState, useMemo } from "react";

type ShapeType = "circle" | "square" | "triangle" | "hexagon" | "ring";

interface Shape {
  id: number;
  type: ShapeType;
  size: number;
  x: number;
  y: number;
  duration: number;
  delay: number;
  direction: "up" | "down" | "left" | "right" | "diagonal";
  rotation: number;
  opacity: number;
}

const ShapeComponent = ({ shape }: { shape: Shape }) => {
  // Alternate between cyan and white for variety
  const isWhiteVariant = shape.id % 3 === 0;
  const primaryColor = isWhiteVariant ? "255, 255, 255" : "46, 194, 255";

  const baseStyle: React.CSSProperties = {
    position: "absolute",
    left: `${shape.x}%`,
    top: `${shape.y}%`,
    width: `${shape.size}px`,
    height: `${shape.size}px`,
    opacity: shape.opacity,
    animation: `move${shape.direction} ${shape.duration}s linear infinite, rotate${shape.id % 2 === 0 ? "CW" : "CCW"} ${shape.duration * 2}s linear infinite`,
    animationDelay: `${shape.delay}s`,
  };

  switch (shape.type) {
    case "circle":
      return (
        <div
          style={{
            ...baseStyle,
            borderRadius: "50%",
            border: `2px solid rgba(${primaryColor}, 0.3)`,
            boxShadow: `0 0 20px rgba(${primaryColor}, 0.2), inset 0 0 20px rgba(${primaryColor}, 0.1)`,
          }}
        />
      );
    case "ring":
      return (
        <div
          style={{
            ...baseStyle,
            borderRadius: "50%",
            border: `3px solid rgba(${primaryColor}, 0.25)`,
            boxShadow: `0 0 30px rgba(${primaryColor}, 0.15)`,
          }}
        />
      );
    case "square":
      return (
        <div
          style={{
            ...baseStyle,
            border: `2px solid rgba(${primaryColor}, 0.25)`,
            borderRadius: "8px",
            boxShadow: `0 0 25px rgba(${primaryColor}, 0.15)`,
            transform: `rotate(${shape.rotation}deg)`,
          }}
        />
      );
    case "triangle":
      return (
        <div
          style={{
            ...baseStyle,
            width: 0,
            height: 0,
            borderLeft: `${shape.size / 2}px solid transparent`,
            borderRight: `${shape.size / 2}px solid transparent`,
            borderBottom: `${shape.size}px solid rgba(${primaryColor}, 0.2)`,
            filter: `drop-shadow(0 0 15px rgba(${primaryColor}, 0.3))`,
            background: "transparent",
          }}
        />
      );
    case "hexagon":
      return (
        <div
          style={{
            ...baseStyle,
            clipPath:
              "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            border: `2px solid rgba(${primaryColor}, 0.3)`,
            background: `rgba(${primaryColor}, 0.05)`,
            boxShadow: `0 0 30px rgba(${primaryColor}, 0.2)`,
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: "3px",
              clipPath:
                "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
              border: `1px solid rgba(${primaryColor}, 0.2)`,
            }}
          />
        </div>
      );
    default:
      return null;
  }
};

export const AnimatedBackground: React.FC = () => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const shapes = useMemo(() => {
    const types: ShapeType[] = [
      "circle",
      "square",
      "triangle",
      "hexagon",
      "ring",
    ];
    const directions: Shape["direction"][] = [
      "up",
      "down",
      "left",
      "right",
      "diagonal",
    ];

    return Array.from({ length: 25 }).map(
      (_, i): Shape => ({
        id: i,
        type: types[i % types.length],
        size: Math.random() * 120 + 60,
        x: Math.random() * 100,
        y: Math.random() * 100,
        duration: Math.random() * 20 + 15,
        delay: Math.random() * 10,
        direction: directions[i % directions.length],
        rotation: Math.random() * 360,
        opacity: Math.random() * 0.4 + 0.2,
      })
    );
  }, []);

  const floatingDots = useMemo(
    () =>
      Array.from({ length: 50 }).map((_, i) => ({
        id: i,
        size: Math.random() * 4 + 2,
        x: Math.random() * 100,
        y: Math.random() * 100,
        duration: Math.random() * 10 + 8,
        delay: Math.random() * 5,
      })),
    []
  );

  if (!mounted) return null;

  return (
    <div className="fixed inset-0 z-0 overflow-hidden bg-black max-h-screen max-w-screen">
      {/* Ambient glow spots - cyan and white */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full animate-glow-pulse-1"
        style={{
          top: "-10%",
          left: "-5%",
          background:
            "radial-gradient(circle, rgba(46, 194, 255, 0.15) 0%, transparent 60%)",
          filter: "blur(60px)",
        }}
      />
      <div
        className="absolute w-[500px] h-[500px] rounded-full animate-glow-pulse-2"
        style={{
          bottom: "-15%",
          right: "-10%",
          background:
            "radial-gradient(circle, rgba(46, 194, 255, 0.12) 0%, transparent 60%)",
          filter: "blur(50px)",
        }}
      />
      {/* White accent glow */}
      <div
        className="absolute w-[400px] h-[400px] rounded-full animate-glow-pulse-1"
        style={{
          top: "40%",
          right: "20%",
          background:
            "radial-gradient(circle, rgba(255, 255, 255, 0.08) 0%, transparent 60%)",
          filter: "blur(40px)",
          animationDelay: "2s",
        }}
      />

      {/* Moving geometric shapes */}
      {shapes.map((shape) => (
        <ShapeComponent key={shape.id} shape={shape} />
      ))}

      {/* Floating dots - mix of cyan and white */}
      {floatingDots.map((dot) => {
        const isWhite = dot.id % 4 === 0;
        return (
          <div
            key={dot.id}
            className="absolute rounded-full"
            style={{
              width: `${dot.size}px`,
              height: `${dot.size}px`,
              left: `${dot.x}%`,
              top: `${dot.y}%`,
              background: isWhite
                ? "rgba(255, 255, 255, 0.9)"
                : "rgba(46, 194, 255, 0.8)",
              boxShadow: isWhite
                ? `0 0 ${dot.size * 3}px rgba(255, 255, 255, 0.4)`
                : `0 0 ${dot.size * 3}px rgba(46, 194, 255, 0.5)`,
              animation: `floatDot ${dot.duration}s ease-in-out infinite`,
              animationDelay: `${dot.delay}s`,
            }}
          />
        );
      })}

      {/* Grid lines */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(46, 194, 255, 1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(46, 194, 255, 1) 1px, transparent 1px)
          `,
          backgroundSize: "100px 100px",
        }}
      />

      {/* Connecting lines effect */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <defs>
          <linearGradient
            id="lineGradientCyan"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="rgba(46, 194, 255, 0)" />
            <stop offset="50%" stopColor="rgba(46, 194, 255, 0.5)" />
            <stop offset="100%" stopColor="rgba(46, 194, 255, 0)" />
          </linearGradient>
          <linearGradient
            id="lineGradientWhite"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="rgba(255, 255, 255, 0)" />
            <stop offset="50%" stopColor="rgba(255, 255, 255, 0.3)" />
            <stop offset="100%" stopColor="rgba(255, 255, 255, 0)" />
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1="30%"
          x2="100%"
          y2="70%"
          stroke="url(#lineGradientCyan)"
          strokeWidth="1"
          className="animate-line-move-1"
        />
        <line
          x1="100%"
          y1="20%"
          x2="0"
          y2="80%"
          stroke="url(#lineGradientWhite)"
          strokeWidth="1"
          className="animate-line-move-2"
        />
        <line
          x1="20%"
          y1="0"
          x2="80%"
          y2="100%"
          stroke="url(#lineGradientCyan)"
          strokeWidth="1"
          className="animate-line-move-3"
        />
      </svg>

      {/* Subtle vignette - no harsh edges */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 120% 100% at 50% 50%, transparent 40%, rgba(0, 0, 0, 0.3) 100%)",
        }}
      />
    </div>
  );
};
